import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { Observable, tap, catchError, of } from 'rxjs';
import { ProductActions } from './product.actions';
import { Product } from '../model/product';
import { ProductFacadeService } from '../service/product-facede.service';

export interface ProductStateModel {
  products: Product[];
  selectedProduct?: Product | null;
  currentProductIndex: number;
  loading: boolean;
  error?: string | null;
}

@State<ProductStateModel>({
  name: 'product',
  defaults: {
    products: [],
    selectedProduct: null,
    currentProductIndex: 0,
    loading: false,
    error: null,
  },
})
@Injectable()
export class ProductState {
  private readonly productFacadeService = inject(ProductFacadeService);

  @Action(ProductActions.LoadProducts)
  public loadProducts(
    ctx: StateContext<ProductStateModel>
  ): Observable<Product[]> {
    ctx.patchState({ loading: true, error: null });

    return this.productFacadeService.getList().pipe(
      tap((products: Product[]) => {
        ctx.patchState({
          products,
          loading: false,
          error: null,
        });
      }),
      catchError(error => {
        ctx.patchState({
          loading: false,
          products: [],
          error: 'Failed to load products. Please try again later.',
        });
        console.error('Failed to load products:', error);
        return of([]);
      })
    );
  }

  @Action(ProductActions.LoadProductByApiName)
  public loadByApiName(
    ctx: StateContext<ProductStateModel>,
    { apiName }: ProductActions.LoadProductByApiName
  ): Observable<Product> {
    ctx.patchState({ loading: true, error: null });

    return this.productFacadeService.getByApiName(apiName).pipe(
      tap((selectedProduct: Product) => {
        ctx.patchState({
          selectedProduct,
          loading: false,
          error: null,
        });
      }),
      catchError(error => {
        ctx.patchState({
          loading: false,
          selectedProduct: null,
          error: 'Failed to load product. Please try again later.',
        });
        console.error('Failed to load product by API name:', error);
        return of({} as Product);
      })
    );
  }

  @Action(ProductActions.SetSelectedProduct)
  public setSelectedProduct(
    ctx: StateContext<ProductStateModel>,
    { selectedProduct }: ProductActions.SetSelectedProduct
  ) {
    ctx.patchState({ selectedProduct });
  }

  @Action(ProductActions.SetCurrentProductIndex)
  public setCurrentProductIndex(
    ctx: StateContext<ProductStateModel>,
    { index }: ProductActions.SetCurrentProductIndex
  ) {
    ctx.patchState({ currentProductIndex: index });
  }

  @Action(ProductActions.NextProduct)
  public nextProduct(ctx: StateContext<ProductStateModel>) {
    const state = ctx.getState();
    const nextIndex =
      state.currentProductIndex + 1 >= state.products.length
        ? 0
        : state.currentProductIndex + 1;

    ctx.patchState({ currentProductIndex: nextIndex });
  }

  @Action(ProductActions.PreviousProduct)
  public previousProduct(ctx: StateContext<ProductStateModel>) {
    const state = ctx.getState();
    const prevIndex =
      state.currentProductIndex - 1 < 0
        ? state.products.length - 1
        : state.currentProductIndex - 1;

    ctx.patchState({ currentProductIndex: prevIndex });
  }
}
